import { createContextStore } from "easy-peasy";
import type { Actions, Dispatch, State, Store } from "easy-peasy";
import type { FC, ReactNode } from "react";

import model from "./model/model";
import type { StoreModel } from "./model/types";

type Injections = object;
type RuntimeModel = { name: string };

/** Return shape of {@link createContextStore} for restaurant store models. */
type RestaurantContextStore<M extends StoreModel = StoreModel> = {
  Provider: FC<{
    children?: ReactNode;
    runtimeModel?: RuntimeModel;
    injections?: Injections | ((previousInjections: Injections) => Injections);
  }>;
  useStore: () => Store<M>;
  useStoreState: <Result = unknown>(
    mapState: (state: State<M>) => Result,
    equalityFn?: (prev: Result, next: Result) => boolean,
  ) => Result;
  useStoreActions: <Result = unknown>(
    mapActions: (actions: Actions<M>) => Result,
  ) => Result;
  useStoreDispatch: () => Dispatch<M>;
  useStoreRehydrated: () => boolean;
};

/**
 * Builds the Easy Peasy context store for {@link StoreModel}. Pass {@link composeModel}
 * to spread extra slices/thunks onto the base {@link model} while staying assignable to
 * {@link StoreModel} (or a subtype that adds composed fields).
 */
export function createRestaurantStore(): RestaurantContextStore<StoreModel>;
export function createRestaurantStore<M extends StoreModel>(
  composeModel: (base: typeof model) => M,
): RestaurantContextStore<M>;
export function createRestaurantStore<M extends StoreModel>(
  composeModel?: (base: typeof model) => M,
): RestaurantContextStore<StoreModel> | RestaurantContextStore<M> {
  if (composeModel) {
    return createContextStore<M, Injections, RuntimeModel>(
      (runtimeModel) => ({
        ...composeModel(model),
        deriveCacheIdentity: Object.create(null) as object,
        name: runtimeModel?.name || "Unnamed Restaurant Store",
      }),
      { name: "RestaurantStore", version: 3 },
    );
  }

  return createContextStore<StoreModel, Injections, RuntimeModel>(
    (runtimeModel) => ({
      ...model,
      deriveCacheIdentity: Object.create(null) as object,
      name: runtimeModel?.name || "Unnamed Restaurant Store",
    }),
    { name: "RestaurantStore", version: 3 },
  );
}

/** Base store with no composed extensions (tests / tooling). */
export default createRestaurantStore();
